import {
  Component,
  Input,
  Output,
  OnInit,
  OnDestroy,
  EventEmitter,
  ChangeDetectorRef,
} from '@angular/core';
import { QuickLink } from '@interfaces/quick-link.interface';
import { TermAutosuggest } from '@classes/term-autosuggest.class';
import { GoogleTagManagerService } from '@services/analytics/google-tag-manager.service';
import { QuickLinksGtmTagEvent } from '@interfaces/quick-links-gtm-tag-event.model';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { ConfigurationService } from '@services/configuration.service';
import { SettingsService } from '@services/settings.service';
import { filter, first, switchMap, map, tap } from 'rxjs/operators';
import { SpecialtiesService } from '@services/specialties/specialties.service';
import { Specialty } from '@interfaces/specialty.model';
import { ProceduresService } from '@services/procedures.service';
import { Procedure } from '@interfaces/procedure.model';
import { NetworksService } from '@services/networks.service';
import { Network } from '@interfaces/network.model';
import { cloneDeep, find, take } from 'lodash';
import { Store } from '@ngrx/store';
import { setSearchMethodAction } from '@store/search/search.actions';
import { SearchMethods } from '@enums/search-methods.enum';
import { SearchState } from '@store/search/search.state';
import { ProductAnalyticsService } from '@services/product-analytics/product-analytics.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-quick-links',
  templateUrl: './quick-links.component.html',
  styleUrls: ['./quick-links.component.scss'],
})
export class QuickLinksComponent implements OnInit, OnDestroy {
  @Input() public dark: boolean = false;
  @Input() public centered: boolean = false;
  @Input() public title: boolean = true;
  @Input() public gtmEventCategorySuffix: string;
  @Input() public maxQuickLinks: number = 6;

  @Output() public hasQuickLinks: EventEmitter<boolean> = new EventEmitter();

  public quickLinks: QuickLink[];
  public focusedElement = null;

  private subscription: Subscription;
  private quickLinkMenuName: string = '';

  constructor(
    private termAutosuggest: TermAutosuggest,
    private googleTagManagerService: GoogleTagManagerService,
    private configService: ConfigurationService,
    private settingsService: SettingsService,
    private specialtiesService: SpecialtiesService,
    private proceduresService: ProceduresService,
    private networksService: NetworksService,
    private changeDetector: ChangeDetectorRef,
    private store: Store<SearchState>,
    private productAnalyticsService: ProductAnalyticsService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.subscribeToQuickLinks();
  }

  ngOnDestroy(): void {
    this.unsubscribeFromQuickLinks();
  }

  public trackByName(item: QuickLink): string {
    return item.name;
  }

  public onQuickLinkClick(link: QuickLink): boolean {
    if (link.params.procedure_id) {
      this.termAutosuggest.activateSearch(
        { type: 'procedures', id: link.params.procedure_id },
        link.params
      );
    } else {
      this.termAutosuggest.activateSearch(
        { type: 'search_specialties', id: link.params.search_specialty_id },
        link.params
      );
    }
    this.store.dispatch(
      setSearchMethodAction({ searchMethod: SearchMethods.CommonSearchLink })
    );
    this.gtmTagEvent(link);
    this.sendPendoTrackEvent(link);
    return false;
  }

  public onQuickLinkMenuClick(link: QuickLink): void {
    this.focusedElement = document.activeElement;
    this.quickLinkMenuName = this.translateQuickLinkName(link.name);
    this.gtmTagEvent(link);
  }

  public handleNavigationKeys(): void {
    this.focusedElement?.focus();
  }

  private subscribeToQuickLinks(): void {
    this.subscription = this.getQuickLinks().subscribe({
      next: (quickLinks: QuickLink[]) => {
        this.quickLinks = quickLinks;
        this.hasQuickLinks.emit(!!(quickLinks && quickLinks.length));
        this.triggerChanges();
      },
      error: () => this.clearQuickLinks(),
    });
  }

  private triggerChanges(): void {
    this.changeDetector.detectChanges();
  }

  private unsubscribeFromQuickLinks(): void {
    this.subscription.unsubscribe();
  }

  private getQuickLinks(): Observable<QuickLink[]> {
    return this.settingsService
      .getSetting('search_quick_links', null, false)
      .pipe(
        tap(() => this.clearQuickLinks()),
        switchMap((quickLinks) => {
          return this.networksService.resolvedNetwork.pipe(
            switchMap((network: Network) => {
              return combineLatest([
                this.getSearchSpecialties(network.id),
                this.getProcedures(),
              ]).pipe(
                map(([specialties, procedures]) =>
                  this.filterQuickLinks(quickLinks, specialties, procedures)
                )
              );
            })
          );
        })
      );
  }

  private getSearchSpecialties(networkId: string): Observable<Specialty[]> {
    const params = { network_id: networkId };
    return this.specialtiesService
      .requestSpecialties(null, params)
      .pipe(first());
  }

  private getProcedures(): Observable<Procedure[]> {
    return this.proceduresService.proceduresUpdated.pipe(
      filter((procedures) => procedures !== undefined)
    );
  }

  private clearQuickLinks(): void {
    this.quickLinks = null;
  }

  // Filter Quick Links and limit to 6 items.
  private filterQuickLinks(
    origQuickLinks: QuickLink[],
    specialties: Specialty[],
    procedures: Procedure[]
  ): QuickLink[] {
    if (!origQuickLinks || origQuickLinks.length === 0) {
      return [];
    }
    let quickLinks = cloneDeep(origQuickLinks);
    quickLinks = this.specialtyCarveoutFilter(quickLinks, specialties);
    quickLinks = this.unavailableProceduresFilter(quickLinks, procedures);
    quickLinks = this.removeEmptyParents(quickLinks);
    if (procedures && procedures.length > 0) {
      quickLinks = this.filterQuickLinksMax(2, quickLinks);
    } else {
      quickLinks = this.filterQuickLinksMax(0, quickLinks);
    }
    quickLinks = take(quickLinks, this.maxQuickLinks);
    return quickLinks;
  }

  // Only include up to 2 procedure items with the rest being non-procedure items.
  private filterQuickLinksMax(
    maxProcedure: number,
    config: QuickLink[]
  ): QuickLink[] {
    const filtered: QuickLink[] = [];
    const maxNonProcedure: number = this.maxNonProcedureCount(
      config,
      maxProcedure
    );
    let nonProcedureCounter: number = 0;
    let procedureCounter: number = 0;
    config.forEach((quickLink: QuickLink) => {
      if (
        !this.isProcedure(quickLink) &&
        nonProcedureCounter < maxNonProcedure
      ) {
        filtered.push(quickLink);
        nonProcedureCounter++;
      }
      if (this.isProcedure(quickLink) && procedureCounter < maxProcedure) {
        filtered.push(quickLink);
        procedureCounter++;
      }
    });
    return filtered;
  }

  // Specialty Carevout Filter
  // Remove all specialties that are not available.
  private specialtyCarveoutFilter(
    quickLinks: QuickLink[],
    specialties: Specialty[]
  ): QuickLink[] {
    return quickLinks.filter((quickLink) => {
      if (quickLink.children && quickLink.children.length) {
        quickLink.children = this.specialtyCarveoutFilter(
          quickLink.children,
          specialties
        );
        return quickLink.children.length > 0;
      }
      if (quickLink.params && quickLink.params.search_specialty_id) {
        const specialtyId = parseInt(quickLink.params.search_specialty_id, 10);
        return !!find(specialties, { id: specialtyId });
      }
      return true;
    });
  }

  // Unavailable Procedure Filter
  // Remove all procedures that are not available.
  private unavailableProceduresFilter(
    quickLinks: QuickLink[],
    procedures: Procedure[]
  ): QuickLink[] {
    return quickLinks.filter((quickLink) => {
      if (quickLink.children && quickLink.children.length) {
        quickLink.children = this.unavailableProceduresFilter(
          quickLink.children,
          procedures
        );
        return quickLink.children.length > 0;
      }
      if (quickLink.params && quickLink.params.procedure_id) {
        const procedureId = parseInt(quickLink.params.procedure_id, 10);
        return !!find(procedures, { id: procedureId });
      }
      return true;
    });
  }

  private maxNonProcedureCount(
    config: QuickLink[],
    maxProcedure: number
  ): number {
    let numProcedures = 0;
    config.forEach((quickLink: QuickLink) => {
      if (this.isProcedure(quickLink)) {
        numProcedures++;
      }
    });

    if (numProcedures >= maxProcedure) {
      return this.maxQuickLinks - maxProcedure;
    } else {
      return this.maxQuickLinks - numProcedures;
    }
  }

  private isProcedure(quickLink: QuickLink): boolean {
    return (
      (quickLink.params && !!quickLink.params.procedure_id) ||
      (quickLink.children &&
        quickLink.children.length &&
        quickLink.children[0] &&
        quickLink.children[0].params &&
        !!quickLink.children[0].params.procedure_id)
    );
  }

  private removeEmptyParents(quickLinks: QuickLink[]): QuickLink[] {
    return quickLinks.filter(
      (quickLink: QuickLink) =>
        !quickLink.children || quickLink.children.length > 0
    );
  }

  private gtmTagEvent(link: QuickLink): void {
    link = this.googleTagManagerService.translate(link, ['name']);
    const event = new QuickLinksGtmTagEvent(link, this.gtmEventCategorySuffix);
    this.googleTagManagerService.pushToDataLayer({
      event: event.name,
      quick_search_option_type: event.type,
      quick_search_option_label: event.label,
    });
  }

  private sendPendoTrackEvent(link: QuickLink): void {
    this.productAnalyticsService.sendTrackEvent('Common Search | Quick Links', {
      common_search_link_click_event: true,
      search_method_click_path: this.buildSearchMethodClickPath(link.name),
    });
  }

  private buildSearchMethodClickPath(name: string): string {
    return this.formatClickPath(this.translateQuickLinkName(name));
  }

  private formatClickPath(quickLinkName: string): string {
    return this.quickLinkMenuName
      ? `${this.quickLinkMenuName} > ${quickLinkName}`
      : quickLinkName;
  }

  private translateQuickLinkName(key: string): string {
    return this.translateService.instant(key);
  }
}
